import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Button,
  Card,
  CardHeader,
  CardTitle,
  Skeleton,
} from '@clouway/ui'
import { Pencil, Plus, Trash2 } from 'lucide-react'

import { addSensor, removeSensor, updateSensor } from '../configEdits'
import type { SensorConfig, SensorType } from '../types'
import { SensorEditForm, type SensorFormValues } from '../components/SensorEditForm'
import { useConfigContext } from './ConfigLayout'

// Sensor ids are the AVL ports 0–3 (Teltonika EYE). inverse only applies to
// magnetStatus / batteryIndication (plan.md §6).
const SENSOR_PORTS = ['0', '1', '2', '3']
const SENSOR_TYPES: { key: SensorType; label: string; inverse: boolean }[] = [
  { key: 'batteryIndication', label: 'trackingObjects.configs.batteryIndication', inverse: true },
  { key: 'batteryVoltage', label: 'trackingObjects.configs.batteryVoltage', inverse: false },
  { key: 'magnetStatus', label: 'trackingObjects.configs.magnetStatus', inverse: true },
  { key: 'temperature', label: 'trackingObjects.configs.temperature', inverse: false },
]

function toMultiplier(value: string): number {
  const n = Number(value)
  return value.trim() === '' || Number.isNaN(n) ? 0 : n
}

export function SensorsConfig() {
  const { t } = useTranslation('feature')
  const { t: tCommon } = useTranslation('common')
  const { config, save, loading } = useConfigContext()
  const [editing, setEditing] = useState<{ type: SensorType; original?: number } | null>(null)

  if (loading) return <Skeleton className="h-40 w-full" />

  const handleSubmit = (values: SensorFormValues) => {
    const item: SensorConfig = {
      id: Number(values.id),
      title: values.title,
      unit: values.unit,
      multiplier: toMultiplier(values.multiplier),
      enabled: values.enabled,
      inverse: values.inverse,
    }
    const next =
      editing!.original !== undefined
        ? updateSensor(config, editing!.type, editing!.original, item)
        : addSensor(config, editing!.type, item)
    return save(next).then(() => setEditing(null))
  }

  return (
    <div className="flex flex-col gap-6">
      {SENSOR_TYPES.map(({ key, label, inverse }) => {
        const sensors = config.sensorConfiguration[key]
        const editingHere = editing?.type === key
        const editingEntry =
          editingHere && editing.original !== undefined ? sensors.find((s) => s.id === editing.original) : undefined
        const used = new Set(sensors.map((s) => String(s.id)))
        const availablePorts = SENSOR_PORTS.filter((p) => !used.has(p) || String(editingEntry?.id) === p)

        return (
          <section key={key} className="flex flex-col gap-2">
            <h2 className="text-sm font-semibold">{tCommon(label as 'trackingObjects.configs.temperature')}</h2>

            {sensors.map((sensor) => (
              <Card key={sensor.id}>
                <CardHeader>
                  <div className="flex items-center justify-between gap-2">
                    <CardTitle className="text-sm">
                      {sensor.title} · {t('sensorForm.port')} {sensor.id}
                    </CardTitle>
                    <div className="flex gap-1">
                      <Button
                        size="icon"
                        variant="ghost"
                        aria-label="edit"
                        onClick={() => setEditing({ type: key, original: sensor.id })}
                      >
                        <Pencil className="size-4" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="icon" variant="ghost" aria-label="delete">
                            <Trash2 className="size-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>{t('trackingObjects.config.deleteSensor')}</AlertDialogTitle>
                            <AlertDialogDescription>
                              {t('trackingObjects.config.deleteSensorWarning')}
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>{tCommon('ui.button.cancel')}</AlertDialogCancel>
                            <AlertDialogAction onClick={() => save(removeSensor(config, key, sensor.id))}>
                              {tCommon('ui.button.confirm')}
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                </CardHeader>
              </Card>
            ))}

            {editingHere ? (
              <SensorEditForm
                availablePorts={availablePorts}
                showInverse={inverse}
                initialValues={
                  editingEntry
                    ? {
                        id: String(editingEntry.id),
                        title: editingEntry.title,
                        unit: editingEntry.unit,
                        multiplier: String(editingEntry.multiplier),
                        enabled: editingEntry.enabled,
                        inverse: editingEntry.inverse,
                      }
                    : undefined
                }
                onSubmit={handleSubmit}
                onCancel={() => setEditing(null)}
              />
            ) : (
              <Button
                variant="outline"
                size="sm"
                disabled={availablePorts.length === 0 || (editing !== null && !editingHere)}
                onClick={() => setEditing({ type: key })}
              >
                <Plus className="size-4" />
                {tCommon('trackingObjects.configs.newSensor')}
              </Button>
            )}
          </section>
        )
      })}
    </div>
  )
}
